/* eslint-disable react/require-default-props */
import React, {memo, ReactNode} from 'react'
import {
  LogoutIcon,
  GlobeIcon,
  HomeIcon,
  GlobeEuropeIcon,
  BillingIcon,
  ButtonPrimary,
  CustomerPageLayout as CustomerPageLayout_,
  Container,
  media,
  SkeletonButton,
  SkeletonLine,
  AddIcon,
} from '@modifi/ui'
import styled from 'styled-components'
import {clientConfig} from '@modifi/client-config'
import {useTranslation} from '@modifi/plugin-i18n'
import {
  ROUTE_PREVIEW_BILLING,
  ROUTE_PREVIEW_DASHBOARD,
  ROUTE_PREVIEW_ORDERS,
  ROUTE_PREVIEW_REPORTS,
  ROUTE_SHIPMENTS,
} from '../../routes/constants'
import {asPageSubject, useAbility} from '../../constants/permissions'
import {
  PAGE_BILLING,
  PAGE_DASHBOARD,
  PAGE_ONBOARDING,
  PAGE_ORDERS,
  PAGE_REPORTS,
} from '../../constants/navigation'
import {CsvFileIcon, CompanyIcon} from '../../ui/icons'

const CustomerPageLayout = CustomerPageLayout_ as React.ComponentType<any>

const resolveDashboardUrl = (usePreview: boolean): string =>
  usePreview || !clientConfig.get('service_hub')
    ? ROUTE_PREVIEW_DASHBOARD
    : `${clientConfig.get('service_hub')}/dashboard`

const resolveOrdersUrl = (usePreview: boolean, ordersPath: string = 'orders'): string =>
  usePreview || !clientConfig.get('service_hub')
    ? ROUTE_PREVIEW_ORDERS
    : `${clientConfig.get('service_hub')}/${ordersPath}`

const resolveReportsUrl = (usePreview: boolean, reportsPath: string = 'payout-report'): string =>
  usePreview || !clientConfig.get('service_hub')
    ? ROUTE_PREVIEW_REPORTS
    : `${clientConfig.get('service_hub')}/${reportsPath}`

const resolveBillingUrl = (usePreview: boolean): string =>
  usePreview || !clientConfig.get('service_billing')
    ? ROUTE_PREVIEW_BILLING
    : `${clientConfig.get('service_billing')}/invoices`

const ContentContainer = styled(Container)`
  padding-top: 40px;
  padding-bottom: 32px;
  ${media.max.xs`
    padding-left: 10px;
    padding-right: 10px;
  `}
`

export type NavigationProps = {
  children: ReactNode
  userFirstName?: string
  userLastName?: string
  companyName?: string
  hasCreatedTrades?: boolean
  isOrderCreationDisabled?: boolean
  userActions?: {canCreateOrder?: boolean; canViewBillingPage?: boolean}
  isReportsEnabled?: boolean
}

const Navigation = ({
  children,
  userFirstName,
  userLastName,
  companyName,
  hasCreatedTrades = false,
  isOrderCreationDisabled = false,
  userActions,
  isReportsEnabled = false,
}: NavigationProps) => {
  const ability = useAbility()
  const {t, ready: tReady} = useTranslation('shared', {useSuspense: false})

  const hasAccessToOnboarding = ability.can('read', asPageSubject(PAGE_ONBOARDING))
  const hasAccessToBilling = ability.can('read', asPageSubject(PAGE_BILLING))
  const hasAccessToReports = ability.can('read', asPageSubject(PAGE_REPORTS))
  // If user does not have access to section then show preview for section OR
  // (has access to section AND it is also enabled)
  const showReports = !hasAccessToReports || (hasAccessToReports && isReportsEnabled)
  const showBilling = !hasAccessToBilling || (hasAccessToBilling && userActions?.canViewBillingPage)

  return (
    <CustomerPageLayout
      logoItem={{
        to: ROUTE_SHIPMENTS,
      }}
      menuItems={[
        // If access token has hub:read access then we target to HUB/BILLING links, else link to preview page
        {
          label: tReady ? t('layout.side-navigation.dashboard.label') : <SkeletonLine />,
          to: resolveDashboardUrl(!ability.can('read', asPageSubject(PAGE_DASHBOARD))),
          icon: HomeIcon,
          'data-ident': 'dashboard-link',
        },
        {
          label: tReady ? t('layout.side-navigation.orders.label') : <SkeletonLine />,
          to: resolveOrdersUrl(
            !ability.can('read', asPageSubject(PAGE_ORDERS)),
            userActions?.canCreateOrder ? 'orders' : 'trade-partner-orders'
          ),
          icon: GlobeEuropeIcon,
          'data-ident': 'orders-link',
        },
        showBilling
          ? {
              label: tReady ? t('layout.side-navigation.billing.label') : <SkeletonLine />,
              to: resolveBillingUrl(!hasAccessToBilling),
              icon: BillingIcon,
              'data-ident': 'billing-link',
            }
          : null,
        showReports
          ? {
              label: tReady ? t('layout.side-navigation.reports.label') : <SkeletonLine />,
              to: resolveReportsUrl(
                !hasAccessToReports,
                hasCreatedTrades ? 'payout-report' : 'repayment-report'
              ),
              icon: CsvFileIcon,
              'data-ident': 'reports-link',
            }
          : null,
        {
          label: tReady ? t('layout.side-navigation.services.label') : <SkeletonLine />,
          to: ROUTE_SHIPMENTS,
          icon: GlobeIcon,
          'data-ident': 'services-link',
          subMenus: [
            {
              label: tReady ? t('layout.side-navigation.shipments.label') : <SkeletonLine />,
              to: ROUTE_SHIPMENTS,
              'data-ident': 'shipment-services-link',
            },
          ],
        },
      ].filter(Boolean)}
      ctaItem={
        userActions?.canCreateOrder
          ? {
              label: tReady ? (
                <ButtonPrimary big data-target="create-order" active={!isOrderCreationDisabled}>
                  {t('layout.top-navigation.cta-create-order')}
                </ButtonPrimary>
              ) : (
                <SkeletonButton>
                  <ButtonPrimary big data-target="create-order-skeleton">
                    Create Trade
                  </ButtonPrimary>
                </SkeletonButton>
              ),
              to: `${clientConfig.get('service_hub')}/create-order`,
              hasButton: true,
              isDisabled: isOrderCreationDisabled,
            }
          : null
      }
      linkItem={
        userActions?.canCreateOrder && !isOrderCreationDisabled
          ? {
              label: tReady ? t('layout.side-navigation.cta-create-order') : <SkeletonLine />,
              to: `${clientConfig.get('service_hub')}/create-order`,
              isExternal: true,
              icon: AddIcon,
            }
          : null
      }
      userDropdown={{
        firstName: userFirstName,
        lastName: userLastName,
        companyName,
        items: [
          clientConfig.get('service_onboarding', false) !== false && hasAccessToOnboarding
            ? {
                label: tReady ? (
                  t('layout.side-navigation.company-details.label')
                ) : (
                  <SkeletonLine />
                ),
                to: `${clientConfig.get('service_onboarding')}?navigationSource=hub`,
                icon: CompanyIcon,
                'data-ident': 'onboarding-link',
              }
            : null,
          {
            label: 'Log out',
            icon: LogoutIcon,
            to: '/logout',
          },
        ].filter(Boolean),
      }}
      disableNotifications
    >
      <ContentContainer>{children!}</ContentContainer>
    </CustomerPageLayout>
  )
}

export default memo(Navigation)
